import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useSelector, useDispatch } from "react-redux";
import { useMemo, useState } from "react";
import { Chip, Grid, Button, Card, CardContent, Stack, TextField, Typography } from "@mui/material";
import { deleteProperties, getProperty } from "../services/propertyService";
import {
  resetForm,
  setAssessorValue,
  setBuildingAddr,
  setCIDValue,
  setContact,
  setContacts,
  setData,
  setOwnerMailing,
  setOwnerName,
  setParcelID,
  setPropertyDesc,
  setSearchStatus,
  setTransferID,
  setExempt,
  setMarker,
  setImportModal,
  setCustomFields,
} from "../features/assessorSlice";
import { countActiveFilters } from "./filterPanel";
import { getCIDProperties } from "../services/cidService";
import { Box } from "@mui/system";
import {
  resetContacts,
  setResults,
  setSearchTerm,
  setSelectedContact,
  setSelectedContacts,
  setTempData,
} from "../features/contactSlice";
import { getContact } from "../services/contactService";
import { ImportExport } from "@mui/icons-material";

const PropertyTable = (props) => {
  const [selected, setSelected] = useState([]);
  const [tableSearch, setTableSearch] = useState("");
  const cid = useSelector((state) => state.assessor.value.form.cid);
  const data = useSelector((state) => state.assessor.value.data);
  const filters = useSelector((state) => state.assessor.value.filters);
  const propertyFieldDefs = useSelector((state) => state.settings.value.propertyFields);
  const contactFieldDefs = useSelector((state) => state.settings.value.contactFields);
  const propertyFields = useMemo(
    () => propertyFieldDefs.filter((f) => !f.archived),
    [propertyFieldDefs]
  );
  const contactFields = useMemo(
    () => contactFieldDefs.filter((f) => !f.archived),
    [contactFieldDefs]
  );
  const activeFieldNames = useMemo(
    () => new Set(propertyFields.map((f) => f.internal_name)),
    [propertyFields]
  );
  const dispatch = useDispatch();

  const activeFilterCount = countActiveFilters(filters, activeFieldNames);

  const filteredRows = useMemo(() => {
    const search = tableSearch.trim().toLowerCase();

    return data.filter((row) => {
      if (search) {
        const fields = [
          row.parcelID,
          row.owner_name,
          row.owner_mailing,
          row.building_address,
          row.property_desc,
          row.name,
          row.company,
          row.phone_number,
          ...Object.values(row.custom_fields || {}),
          ...Object.values(row.contact_custom_fields || {}),
        ];
        const hit = fields.some((value) =>
          String(value ?? "").toLowerCase().includes(search)
        );
        if (!hit) {
          return false;
        }
      }

      if (filters.markers.length) {
        const marker = row.flag_color || "none";
        if (!filters.markers.includes(marker)) {
          return false;
        }
      }

      for (const [name, filter] of Object.entries(filters.custom)) {
        if (!activeFieldNames.has(name)) {
          continue; // stale filter on an archived/removed field — ignore
        }
        const value = row.custom_fields?.[name];
        if (filter.q !== undefined) {
          if (!String(value ?? "").toLowerCase().includes(filter.q.toLowerCase())) {
            return false;
          }
        }
        if (filter.min !== undefined || filter.max !== undefined) {
          const num = typeof value === "number" ? value : parseFloat(value);
          if (!Number.isFinite(num)) return false;
          if (filter.min !== undefined && num < filter.min) return false;
          if (filter.max !== undefined && num > filter.max) return false;
        }
        if (filter.from !== undefined || filter.to !== undefined) {
          const date = String(value || "");
          if (!date) return false;
          if (filter.from !== undefined && date < filter.from) return false;
          if (filter.to !== undefined && date > filter.to) return false;
        }
        if (filter.selected !== undefined) {
          if (!filter.selected.includes(String(value ?? ""))) return false;
        }
      }

      return true;
    });
  }, [data, tableSearch, filters, activeFieldNames]);

  const columns = useMemo(() => [
    {
      field: "flag_color",
      width: 50,
      headerName: "Mark",
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor: params.value ? params.value : "gray",
            minWidth: "100%",
            borderRadius: 10,
          }}
        >
          &nbsp;
        </Box>
      ),
    },
    {
      field: "last_updated",
      headerName: "Last Updated",
      type: "date",
      width: 100,
      valueGetter: ({ value }) => value && new Date(value),
    },
    { field: "parcelID", headerName: "Parcel ID", width: 150 },
    { field: "owner_name", headerName: "Owner Name", width: 150 },
    { field: "owner_mailing", headerName: "Mailing Address", width: 150 },
    { field: "building_address", headerName: "Building Address", width: 150 },
    { field: "assessor_value", headerName: "Assessor Value", width: 150 },
    { field: "cid_value", headerName: "CID Value", width: 100 },
    { field: "property_desc", headerName: "Property Desc", width: 150 },

    { field: "name", headerName: "Name", width: 150 },
    { field: "phone_number", headerName: "Phone Number", width: 150 },
    { field: "email", headerName: "Email", width: 150 },
    { field: "company", headerName: "Company", width: 150 },
    { field: "secondary_contact", headerName: "Secondary Contact", width: 150 },
    { field: "secondary_phone", headerName: "Secondary Phone", width: 150 },
    { field: "secondary_email", headerName: "Secondary Email", width: 150 },
    { field: "notes", headerName: "Notes", width: 150 },
    { field: "callsMade", headerName: "Calls Made", width: 150, valueGetter: ({ value }) => value ? value : 0 },
    { field: "followUpCalls", headerName: "Follow Up Calls", width: 150, valueGetter: ({ value }) => value ? value : 0 },
    { field: "emailsSent", headerName: "Emails Sent", width: 150, valueGetter: ({ value }) => value ? value : 0 },
    { field: "dropIns", headerName: "Drops In", width: 150, valueGetter: ({ value }) => value ? value : 0 },

    ...propertyFields.map((f) => ({
      field: `cf_${f.internal_name}`,
      headerName: f.label,
      width: 150,
      valueGetter: (params) => params.row.custom_fields?.[f.internal_name] ?? "",
    })),
    ...contactFields.map((f) => ({
      field: `ccf_${f.internal_name}`,
      headerName: `${f.label} (Contact)`,
      width: 150,
      valueGetter: (params) => params.row.contact_custom_fields?.[f.internal_name] ?? "",
    })),
  ], [propertyFields, contactFields]);

  const handleSelect = async (p) => {
    props.toggleLookup(true);
    if (p.length !== 0) {
      dispatch(resetContacts());
      dispatch(resetForm());
      setSelected(p);
      dispatch(setTransferID(p[p.length - 1]));
      await getProperty(p[p.length - 1]).then(async (property) => {
        dispatch(setParcelID(property.parcelID));
        dispatch(setOwnerName(property.owner_name));
        dispatch(setOwnerMailing(property.owner_mailing));
        dispatch(setCIDValue(property.cid_value));
        dispatch(setMarker(property.flag_color));
        dispatch(setCustomFields(property.custom_fields || {}));
        dispatch(setBuildingAddr(property.building_address));
        dispatch(setPropertyDesc(property.property_desc));
        dispatch(setAssessorValue(property.assessor_value));
        const contactIds = Array.isArray(property.contact_ids)
          ? property.contact_ids
          : property.contact_id
            ? [property.contact_id]
            : [];

        dispatch(setContacts(contactIds));
        dispatch(setSelectedContacts(contactIds));
        dispatch(setSelectedContact(contactIds.length ? contactIds[0] : null));

        if (contactIds.length) {
          const contactRows = (
            await Promise.all(contactIds.map((contactId) => getContact(contactId)))
          ).filter(Boolean);
          if (contactRows.length) {
            dispatch(setSearchTerm(contactRows[0].name || ""));
            dispatch(setTempData(contactRows[0]));
            dispatch(setResults(contactRows));
          } else {
            dispatch(setSearchTerm(""));
            dispatch(setTempData(null));
            dispatch(setResults([]));
          }
        } else {
          dispatch(setSearchTerm(""));
          dispatch(setTempData(null));
          dispatch(setResults([]));
          dispatch(setContact(null));
        }
        if (parseFloat(property.cid_value) === 0) {
          dispatch(setExempt(true));
        } else {
          dispatch(setExempt(false));
        }
        dispatch(setSearchStatus(2));
      });
    } else {
      dispatch(resetForm());
      dispatch(resetContacts());
      dispatch(setContacts([]));
    }
  };

  return (
    <Grid container mb={2} spacing={1}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6">
                  Property Results ({filteredRows.length}/{data.length})
                </Typography>
                {activeFilterCount > 0 ? (
                  <Chip
                    size="small"
                    color="primary"
                    label={`${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""} on`}
                  />
                ) : null}
              </Stack>
              <TextField
                size="small"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                label="Filter rows"
                placeholder="Parcel, owner, address, contact..."
                sx={{ minWidth: { xs: "100%", md: 320 } }}
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sx={{ height: { xs: 520, md: 760 } }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSize={20}
          rowsPerPageOptions={[20]}
          checkboxSelection
          onSelectionModelChange={(p) => handleSelect(p)}
          disableSelectionOnClick
          sx={{
            backgroundColor: "white",
            borderRadius: 1,
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: "last_updated", sort: "desc" }],
            },
          }}
          components={{
            Toolbar: () => {
              return cid ? (
                <GridToolbarContainer>
                  <Button
                    color="success"
                    startIcon={<ImportExport />}
                    onClick={() => dispatch(setImportModal(true))}
                  >
                    Import
                  </Button>
                  <GridToolbarExport />
                  <Button
                    color="error"
                    onClick={async () => {
                      deleteProperties(selected).then(async () => {
                        const refreshProperties = await getCIDProperties(cid);
                        dispatch(setData(refreshProperties.data));
                      });
                    }}
                  >
                    Delete Row
                  </Button>
                </GridToolbarContainer>
              ) : null;
            },
          }}
        />
      </Grid>

      {/* <Grid item> MOVE TO LOOKUP TOOL
        <Button sx={{ m: 1 }} variant="outlined">
          Import CSV
          <input style={{ opacity: "0", zIndex: 1, position: "absolute" }} type="file" accept=".csv" onChange={(e) => importFile(e.target.files[0])} />
        </Button>
      </Grid> */}
    </Grid>
  );
};

export default PropertyTable;
